import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectAdminToken } from "../../store/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { setupAppPageBodyBackground } from "../game/gameStageBackground";
import deskBg from "../hall_1/stage5/stage5_desk.png";
import mobBg from "../hall_1/stage5/stage5_mob.png";
import { VILLAGE_SQUARE_POINTS } from "../hall_1/villageSquareData";
import { RIVERSIDE_POINTS } from "../hall_1/riversideData";
import { FESTIVAL_MARKET_POINTS } from "../hall_1/festivalMarketData";
import { CELEBRATION_STEPS_POINTS } from "../hall_1/celebrationStepsData";
import { GRAND_CELEBRATION_POINTS } from "../hall_1/grandCelebrationData";
import "../fonts/breuer-headline.css";
import "./leaderboard.css";

const TOTAL_KEYS = 5;
const LEADERBOARD_TOP_N = 10;
/** Highest possible score: every stage's points added up. */
const MAX_SCORE =
  VILLAGE_SQUARE_POINTS +
  RIVERSIDE_POINTS +
  FESTIVAL_MARKET_POINTS +
  CELEBRATION_STEPS_POINTS +
  GRAND_CELEBRATION_POINTS;

function readSessionUserId() {
  let u = {};
  try {
    u = JSON.parse(sessionStorage.getItem("userData") || "{}") || {};
  } catch {
    u = {};
  }
  const id = u?.userId ?? u?.userid ?? u?.id;
  if (id == null || id === "") return "";
  return String(id).trim();
}

function normalizeLeaderboardRow(row, index) {
  if (!row || typeof row !== "object") {
    return {
      key: `row-${index}`,
      rank: index + 1,
      userId: "",
      name: "—",
      points: 0,
      time: "00:00",
      keys: 0,
      completed: false,
    };
  }
  const name =
    row.name ??
    row.player_name ??
    row.user_name ??
    row.display_name ??
    (row.email ? String(row.email).split("@")[0] : null) ??
    "—";
  const rawPoints = row.points ?? row.score ?? row.total_score ?? row.point ?? 0;
  const n = Number(rawPoints);
  const points = Number.isFinite(n) ? n : 0;
  const rawTime = row.time ?? row.display_time ?? "00:00";
  const time = String(rawTime).trim() || "00:00";
  const rankNum = Number(row.rank ?? row.rk);
  const rank = Number.isFinite(rankNum) && rankNum > 0 ? rankNum : index + 1;
  const userId =
    row.userId != null && row.userId !== ""
      ? String(row.userId)
      : row.userid != null && row.userid !== ""
        ? String(row.userid)
        : "";
  const keysNum = Number(row.keys);
  const keys = Number.isFinite(keysNum) ? Math.max(0, Math.min(TOTAL_KEYS, keysNum)) : 0;

  return {
    key: String(row.userId ?? row.userid ?? row.rank ?? row.id ?? index),
    rank,
    userId,
    name: String(name),
    points,
    time,
    keys,
    completed: row.completed != null ? Boolean(row.completed) : keys >= TOTAL_KEYS,
  };
}

function Leaderboard() {
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(() => readSessionUserId());

  const backendBase = useMemo(
    () => String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, ""),
    []
  );

  /* Header BACK leaves the game (platform link), never back into Stage 5. */
  useEffect(() => {
    let redirect = null;
    try {
      redirect = JSON.parse(sessionStorage.getItem("userData") || "{}")?.backButtonRedirect || null;
    } catch {
      redirect = null;
    }
    dispatch(setBackButtonUrl(redirect || process.env.REACT_APP_BASE_URL || null));
  }, [dispatch]);

  useEffect(() => {
    document.body.classList.add("leaderboard-page");
    const cleanupBg = setupAppPageBodyBackground(document.body, {
      deskForeground: deskBg,
      mobForeground: mobBg,
    });
    const t = window.setTimeout(() => {
      const mob =
        window.innerWidth > 0 &&
        window.innerWidth <= 768 &&
        window.matchMedia("(orientation: portrait)").matches;
      document.body.style.setProperty(
        "background-image",
        `url("${mob ? mobBg : deskBg}")`,
        "important"
      );
      document.body.style.setProperty("background-size", "cover", "important");
      document.body.style.setProperty("background-position", "center center", "important");
      document.body.style.setProperty("background-repeat", "no-repeat", "important");
      document.body.style.setProperty("background-attachment", "fixed", "important");
    }, 0);
    return () => {
      window.clearTimeout(t);
      cleanupBg?.();
      document.body.classList.remove("leaderboard-page");
      document.body.style.removeProperty("background-image");
    };
  }, []);

  const loadLeaderboard = useCallback(async () => {
    if (!adminToken || !backendBase) {
      setLoading(false);
      setError(!adminToken ? "Not signed in." : "Server URL not configured.");
      return;
    }
    setLoading(true);
    setError(null);
    const uid = readSessionUserId();
    setCurrentUserId(uid);
    try {
      const res = await fetch(`${backendBase}/getLeaderboard`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          userId: uid || undefined,
          limit: LEADERBOARD_TOP_N,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success && Array.isArray(data.data)) {
        setRows(data.data.map(normalizeLeaderboardRow));
      } else {
        setError(data.error || data.message || "Failed to load leaderboard.");
      }
    } catch (e) {
      setError(e.message || "Failed to load leaderboard.");
    } finally {
      setLoading(false);
    }
  }, [adminToken, backendBase]);

  useEffect(() => {
    loadLeaderboard();
  }, [loadLeaderboard]);

  const isYou = useCallback(
    (r) => Boolean(currentUserId && r.userId && String(r.userId) === String(currentUserId)),
    [currentUserId]
  );

  const myRow = useMemo(() => rows.find(isYou) || null, [rows, isYou]);

  let body;
  if (loading) body = <p className="lb-status">Loading your score…</p>;
  else if (error) body = <p className="lb-status lb-status--error">{error}</p>;
  else if (!myRow) {
    body = <p className="lb-status">Your score will appear here once you play the journey.</p>;
  } else {
    body = (
      <div className="lb-score">
        <p className="lb-score__line">
          Your score is <strong className="lb-score__num">{myRow.points.toLocaleString()}</strong> out of{" "}
          <strong className="lb-score__num">{MAX_SCORE}</strong> in{" "}
          <strong className="lb-score__num">{myRow.time}</strong> minutes.
        </p>
      </div>
    );
  }

  return (
    <div className="lb-page">
      <section className="lb-board" aria-labelledby="lb-title">
        <header className="lb-banner">
          <h1 id="lb-title" className="lb-banner__title">
            Your Score
          </h1>
        </header>

        <p className="lb-subtitle">
          <span className="lb-subtitle__gem" aria-hidden="true" />
          Journey of New Beginnings
          <span className="lb-subtitle__gem" aria-hidden="true" />
        </p>

        <div className="lb-panel">
          {body}
        </div>
      </section>
    </div>
  );
}

export default Leaderboard;
